import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Search,
  Loader2,
  AlertTriangle,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  Merge,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { PageWrapper } from "@/components/layout/PageWrapper";
import {
  searchMergeTitles,
  compareMergeRecords,
  getMergeAssociations,
  executeMerge,
  MergeSearchResult,
  MergeFieldComparison,
} from "@/lib/api";

// ============================================================================
// Helper Functions
// ============================================================================

const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString) return "N/A";
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "Invalid date";
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    }).format(date);
  } catch {
    return "Error";
  }
};

const formatValue = (value: any): string => {
  if (value === null || value === undefined) return "(empty)";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
};

// ============================================================================
// Search Panel Component
// ============================================================================

interface SearchPanelProps {
  onSelectRecords: (records: MergeSearchResult[]) => void;
}

const SearchPanel = ({ onSelectRecords }: SearchPanelProps) => {
  const [searchType, setSearchType] = useState<"name" | "titleId" | "projectNo">("name");
  const [searchValue, setSearchValue] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [hasSearched, setHasSearched] = useState(false);

  const {
    data: searchResults,
    isLoading,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["mergeSearch", searchType, searchValue],
    queryFn: () => searchMergeTitles(searchType, searchValue, 100),
    enabled: false,
  });

  const handleSearch = () => {
    if (searchValue.trim()) {
      setHasSearched(true);
      setSelectedIds(new Set());
      refetch();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const toggleSelection = (id: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else if (newSelected.size < 2) {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const handleCompare = () => {
    if (selectedIds.size === 2 && searchResults?.results) {
      const selectedRecords = searchResults.results.filter((r) =>
        selectedIds.has(r.id)
      );
      onSelectRecords(selectedRecords);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Search Titles</CardTitle>
        <CardDescription>
          Find titles to merge by searching for name, Title ID, or Project Number
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex gap-3">
          <Select
            value={searchType}
            onValueChange={(v) => setSearchType(v as typeof searchType)}
          >
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="titleId">Title ID</SelectItem>
              <SelectItem value="projectNo">Project #</SelectItem>
            </SelectContent>
          </Select>
          <Input
            placeholder={`Enter ${searchType === "name" ? "name" : searchType === "titleId" ? "Title ID" : "Project Number"}...`}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1"
          />
          <Button onClick={handleSearch} disabled={!searchValue.trim() || isFetching}>
            {isFetching ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Search className="h-4 w-4" />
            )}
            <span className="ml-2">Search</span>
          </Button>
        </div>

        {isLoading && (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        )}

        {hasSearched && searchResults && (
          <>
            <div className="text-sm text-muted-foreground">
              Found {searchResults.total} result{searchResults.total !== 1 ? "s" : ""}
            </div>

            {searchResults.results.length > 0 && (
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12"></TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead className="w-24">Title ID</TableHead>
                      <TableHead className="w-28">Project #</TableHead>
                      <TableHead className="w-32">Created</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {searchResults.results.map((record) => (
                      <TableRow
                        key={record.id}
                        className={`cursor-pointer ${selectedIds.has(record.id) ? "bg-blue-50" : ""}`}
                        onClick={() => toggleSelection(record.id)}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedIds.has(record.id)}
                            onCheckedChange={() => toggleSelection(record.id)}
                            disabled={!selectedIds.has(record.id) && selectedIds.size >= 2}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{record.name}</TableCell>
                        <TableCell>{record.titleId ?? "-"}</TableCell>
                        <TableCell>{record.projectNo ?? "-"}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {formatDate(record.createdOn)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {searchResults.results.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No titles found matching your search criteria.
              </div>
            )}
          </>
        )}

        {selectedIds.size === 2 && (
          <div className="flex justify-end">
            <Button onClick={handleCompare}>
              Compare Selected ({selectedIds.size})
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================================================
// Comparison View Component
// ============================================================================

interface ComparisonViewProps {
  recordA: MergeSearchResult;
  recordB: MergeSearchResult;
  onBack: () => void;
  onMergeComplete: () => void;
}

const ComparisonView = ({ recordA, recordB, onBack, onMergeComplete }: ComparisonViewProps) => {
  const { toast } = useToast();
  // Which record to keep: 'A' or 'B'
  const [keepRecord, setKeepRecord] = useState<'A' | 'B'>('A');
  // For each field with differences, which record's value to use: 'A' or 'B'
  // Defaults to the kept record's value
  const [fieldSelections, setFieldSelections] = useState<Record<string, 'A' | 'B'>>({});
  const [showDifferencesOnly, setShowDifferencesOnly] = useState(true);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [contractsOpen, setContractsOpen] = useState(true);
  const [productsOpen, setProductsOpen] = useState(true);

  const keepRecordData = keepRecord === 'A' ? recordA : recordB;
  const removeRecordData = keepRecord === 'A' ? recordB : recordA;

  // Clear field selections when keep record changes
  const handleKeepRecordChange = (newKeep: 'A' | 'B') => {
    if (newKeep !== keepRecord) {
      setKeepRecord(newKeep);
      setFieldSelections({}); // Reset to defaults (will use kept record's values)
    }
  };

  // Fetch comparison data
  const {
    data: comparison,
    isLoading: comparisonLoading,
    error: comparisonError,
  } = useQuery({
    queryKey: ["mergeCompare", recordA.id, recordB.id],
    queryFn: () => compareMergeRecords(recordA.id, recordB.id),
  });

  // Fetch associations for the record being removed
  const removeRecordId = keepRecord === 'A' ? recordB.id : recordA.id;
  const {
    data: associations,
    isLoading: associationsLoading,
  } = useQuery({
    queryKey: ["mergeAssociations", removeRecordId],
    queryFn: () => getMergeAssociations(removeRecordId),
    staleTime: 0,
  });

  // Calculate which fields need to be copied from the removed record to the kept record
  const getFieldsToMerge = (): string[] => {
    if (!comparison) return [];
    const fieldsToMerge: string[] = [];
    for (const field of comparison.differences) {
      const selectedSource = fieldSelections[field.field] || keepRecord;
      // If we selected the removed record's value, we need to merge that field
      if (selectedSource !== keepRecord) {
        fieldsToMerge.push(field.field);
      }
    }
    return fieldsToMerge;
  };

  // Execute merge mutation
  const winnerId = keepRecord === 'A' ? recordA.id : recordB.id;
  const loserId = keepRecord === 'A' ? recordB.id : recordA.id;
  const mergeMutation = useMutation({
    mutationFn: () => executeMerge(winnerId, loserId, getFieldsToMerge()),
    onSuccess: (result) => {
      if (result.success) {
        toast({
          title: "Merge Successful",
          description: `Merged ${result.fieldsUpdated} field(s), moved ${result.associationsMoved.contracts + (result.associationsMoved.products || 0)} association(s).`,
        });
        onMergeComplete();
      } else {
        toast({
          title: "Merge Failed",
          description: result.error || "An error occurred during merge.",
          variant: "destructive",
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Merge Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const setFieldSelection = (field: string, source: 'A' | 'B') => {
    setFieldSelections(prev => ({
      ...prev,
      [field]: source
    }));
  };

  const getFieldSelection = (field: string): 'A' | 'B' => {
    return fieldSelections[field] || keepRecord;
  };

  const getDisplayFields = (): MergeFieldComparison[] => {
    if (!comparison) return [];
    return showDifferencesOnly ? comparison.differences : comparison.allFields;
  };

  // Count how many fields will be taken from the removed record
  const fieldsFromRemovedCount = getFieldsToMerge().length;

  const handleMerge = () => {
    setShowConfirmDialog(true);
  };

  const confirmMerge = () => {
    setShowConfirmDialog(false);
    mergeMutation.mutate();
  };

  if (comparisonLoading) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={onBack}>
          <ChevronLeft className="h-4 w-4 mr-2" /> Back to Search
        </Button>
        <div className="space-y-2">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  if (comparisonError) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" onClick={onBack}>
          <ChevronLeft className="h-4 w-4 mr-2" /> Back to Search
        </Button>
        <div className="p-6 bg-red-50 rounded-lg text-red-700">
          <AlertTriangle className="h-5 w-5 inline mr-2" />
          Failed to compare records: {(comparisonError as Error).message}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={onBack}>
        <ChevronLeft className="h-4 w-4 mr-2" /> Back to Search
      </Button>

      {/* Record Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Select Record to Keep</CardTitle>
          <CardDescription>
            Choose which record will be kept. The other record's data will be merged in and then deleted.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            {/* Record A */}
            <div
              className={`p-4 border rounded-lg cursor-pointer transition-all ${
                keepRecord === 'A'
                  ? "border-green-500 bg-green-50 ring-2 ring-green-200"
                  : "border-red-200 bg-red-50/30 hover:border-red-300"
              }`}
              onClick={() => handleKeepRecordChange('A')}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={keepRecord === 'A'}
                    onChange={() => handleKeepRecordChange('A')}
                    className="h-4 w-4"
                  />
                  <span className="font-semibold">Record A</span>
                </div>
                <Badge variant={keepRecord === 'A' ? "default" : "destructive"} className={keepRecord === 'A' ? "bg-green-600" : ""}>
                  {keepRecord === 'A' ? "KEEP" : "REMOVE"}
                </Badge>
              </div>
              <div className="text-sm space-y-1">
                <div className="font-medium">{recordA.name}</div>
                <div className="text-muted-foreground">Title ID: {recordA.titleId ?? "N/A"}</div>
                <div className="text-muted-foreground">Created: {formatDate(recordA.createdOn)}</div>
              </div>
            </div>

            {/* Record B */}
            <div
              className={`p-4 border rounded-lg cursor-pointer transition-all ${
                keepRecord === 'B'
                  ? "border-green-500 bg-green-50 ring-2 ring-green-200"
                  : "border-red-200 bg-red-50/30 hover:border-red-300"
              }`}
              onClick={() => handleKeepRecordChange('B')}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <input
                    type="radio"
                    checked={keepRecord === 'B'}
                    onChange={() => handleKeepRecordChange('B')}
                    className="h-4 w-4"
                  />
                  <span className="font-semibold">Record B</span>
                </div>
                <Badge variant={keepRecord === 'B' ? "default" : "destructive"} className={keepRecord === 'B' ? "bg-green-600" : ""}>
                  {keepRecord === 'B' ? "KEEP" : "REMOVE"}
                </Badge>
              </div>
              <div className="text-sm space-y-1">
                <div className="font-medium">{recordB.name}</div>
                <div className="text-muted-foreground">Title ID: {recordB.titleId ?? "N/A"}</div>
                <div className="text-muted-foreground">Created: {formatDate(recordB.createdOn)}</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Field Comparison */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg">Field Comparison</CardTitle>
              <CardDescription>
                For each field, select which record's value to use in the final merged record
              </CardDescription>
            </div>
            <Select
              value={showDifferencesOnly ? "differences" : "all"}
              onValueChange={(v) => setShowDifferencesOnly(v === "differences")}
            >
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="differences">Differences Only</SelectItem>
                <SelectItem value="all">All Fields</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {getDisplayFields().length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {showDifferencesOnly
                ? "No differences found between the two records."
                : "No fields to display."}
            </div>
          ) : (
            <div className="border rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-48">Field</TableHead>
                    <TableHead>Record A</TableHead>
                    <TableHead>Record B</TableHead>
                    <TableHead className="w-36 text-center">Use Value From</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getDisplayFields().map((field) => {
                    const valueA = field.valueA;
                    const valueB = field.valueB;
                    const selectedSource = getFieldSelection(field.field);

                    // Can select if: different and not a lookup field
                    const canSelect = field.isDifferent && !field.isLookup;
                    // Has meaningful choice: both values exist (not both empty)
                    const hasChoice = canSelect && (valueA !== null || valueB !== null);

                    return (
                      <TableRow
                        key={field.field}
                        className={field.isDifferent ? "bg-yellow-50" : ""}
                      >
                        <TableCell className="font-medium text-sm">
                          {field.fieldDisplayName}
                          <div className="text-xs text-muted-foreground font-mono">
                            {field.field}
                          </div>
                          {field.isLookup && (
                            <Badge variant="outline" className="text-xs mt-1">Lookup</Badge>
                          )}
                        </TableCell>
                        <TableCell
                          className={`text-sm max-w-xs truncate ${selectedSource === 'A' && field.isDifferent ? 'font-medium text-green-700' : ''}`}
                          title={formatValue(valueA)}
                        >
                          {formatValue(valueA)}
                        </TableCell>
                        <TableCell
                          className={`text-sm max-w-xs truncate ${selectedSource === 'B' && field.isDifferent ? 'font-medium text-green-700' : ''}`}
                          title={formatValue(valueB)}
                        >
                          {formatValue(valueB)}
                        </TableCell>
                        <TableCell className="text-center">
                          {hasChoice ? (
                            <Select
                              value={selectedSource}
                              onValueChange={(v) => setFieldSelection(field.field, v as 'A' | 'B')}
                            >
                              <SelectTrigger className="w-28 h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="A">Record A</SelectItem>
                                <SelectItem value="B">Record B</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : field.isLookup && field.isDifferent ? (
                            <span
                              className="text-muted-foreground text-xs cursor-help"
                              title="Lookup fields cannot be directly merged"
                            >
                              N/A
                            </span>
                          ) : (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Associated Records */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Associated Records</CardTitle>
          <CardDescription>
            These records reference Record {keepRecord === 'A' ? 'B' : 'A'} and will be reassigned to Record {keepRecord}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {associationsLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : (
            <>
              {/* Contracts */}
              <Collapsible open={contractsOpen} onOpenChange={setContractsOpen}>
                <CollapsibleTrigger className="flex items-center gap-2 w-full p-3 bg-muted rounded-lg hover:bg-muted/80">
                  {contractsOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                  <span className="font-medium">Contracts</span>
                  <Badge variant="secondary" className="ml-auto">
                    {associations?.contracts.length ?? 0}
                  </Badge>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  {associations?.contracts && associations.contracts.length > 0 ? (
                    <div className="border rounded-lg mt-2 overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Contract Name</TableHead>
                            <TableHead className="w-40">Currently References</TableHead>
                            <TableHead className="w-40">After Merge</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {associations.contracts.map((contract) => (
                            <TableRow key={contract.id}>
                              <TableCell>{contract.name}</TableCell>
                              <TableCell className="text-muted-foreground">
                                Record {keepRecord === 'A' ? 'B' : 'A'}
                              </TableCell>
                              <TableCell className="text-green-600 font-medium">
                                <ArrowRight className="h-4 w-4 inline mr-1" />
                                Record {keepRecord}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted-foreground text-sm">
                      No contracts associated with the record being removed
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>

              {/* Products */}
              <Collapsible open={productsOpen} onOpenChange={setProductsOpen}>
                <CollapsibleTrigger className="flex items-center gap-2 w-full p-3 bg-muted rounded-lg hover:bg-muted/80">
                  {productsOpen ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                  <span className="font-medium">Products</span>
                  <Badge variant="secondary" className="ml-auto">
                    {associations?.products?.length ?? 0}
                  </Badge>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  {associations?.products && associations.products.length > 0 ? (
                    <div className="border rounded-lg mt-2 overflow-hidden">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Product Name</TableHead>
                            <TableHead className="w-40">Currently References</TableHead>
                            <TableHead className="w-40">After Merge</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {associations.products.map((product) => (
                            <TableRow key={product.id}>
                              <TableCell>{product.name}</TableCell>
                              <TableCell className="text-muted-foreground">
                                Record {keepRecord === 'A' ? 'B' : 'A'}
                              </TableCell>
                              <TableCell className="text-green-600 font-medium">
                                <ArrowRight className="h-4 w-4 inline mr-1" />
                                Record {keepRecord}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted-foreground text-sm">
                      No products associated with the record being removed
                    </div>
                  )}
                </CollapsibleContent>
              </Collapsible>
            </>
          )}
        </CardContent>
      </Card>

      {/* Warning and Actions */}
      <div className="flex items-center justify-between p-4 bg-amber-50 rounded-lg border border-amber-200">
        <div className="flex items-center gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <div>
            <div className="font-medium text-amber-800">
              Record {keepRecord === 'A' ? 'B' : 'A'} ({removeRecordData.name}) will be DELETED
            </div>
            <div className="text-sm text-amber-700">
              This action cannot be undone. Make sure you have selected the correct records.
            </div>
          </div>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onBack}>
            Cancel
          </Button>
          <Button
            onClick={handleMerge}
            disabled={mergeMutation.isPending}
            className="bg-amber-600 hover:bg-amber-700"
          >
            {mergeMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Merge className="h-4 w-4 mr-2" />
            )}
            Merge Records
          </Button>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Merge</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>You are about to merge these records:</p>
                <div className="p-3 bg-muted rounded-lg text-sm space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge className="bg-green-600">KEEP</Badge>
                    <span>Record {keepRecord}: {keepRecordData.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="destructive">REMOVE</Badge>
                    <span>Record {keepRecord === 'A' ? 'B' : 'A'}: {removeRecordData.name}</span>
                  </div>
                </div>
                <p>
                  <strong>{fieldsFromRemovedCount}</strong> field value(s) will be taken from Record {keepRecord === 'A' ? 'B' : 'A'}.
                </p>
                <p>
                  <strong>{(associations?.contracts.length ?? 0) + (associations?.products?.length ?? 0)}</strong> associated record(s) will be reassigned.
                </p>
                <p className="text-red-600 font-medium">
                  Record {keepRecord === 'A' ? 'B' : 'A'} will be permanently deleted. This cannot be undone.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmMerge}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Confirm Merge
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ============================================================================
// Success View Component
// ============================================================================

interface SuccessViewProps {
  onStartNew: () => void;
}

const SuccessView = ({ onStartNew }: SuccessViewProps) => {
  return (
    <Card className="max-w-lg mx-auto mt-12">
      <CardContent className="text-center py-12">
        <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto mb-4" />
        <h2 className="text-xl font-semibold mb-2">Merge Complete</h2>
        <p className="text-muted-foreground mb-6">
          The records have been successfully merged. The removed record has been deleted.
        </p>
        <Button onClick={onStartNew}>
          Start New Merge
        </Button>
      </CardContent>
    </Card>
  );
};

// ============================================================================
// Main Page Component
// ============================================================================

type ViewState = "search" | "compare" | "success";

export default function RecordMergePage() {
  const [viewState, setViewState] = useState<ViewState>("search");
  const [selectedRecords, setSelectedRecords] = useState<MergeSearchResult[]>([]);

  const handleSelectRecords = (records: MergeSearchResult[]) => {
    setSelectedRecords(records);
    setViewState("compare");
  };

  const handleBack = () => {
    setViewState("search");
    setSelectedRecords([]);
  };

  const handleMergeComplete = () => {
    setViewState("success");
    setSelectedRecords([]);
  };

  const handleStartNew = () => {
    setViewState("search");
    setSelectedRecords([]);
  };

  return (
    <PageWrapper>
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold">Record Merge</h1>
          <p className="text-muted-foreground">
            Search for duplicate titles, compare them side-by-side, and merge them into a single record.
          </p>
        </div>

        {viewState === "search" && (
          <SearchPanel onSelectRecords={handleSelectRecords} />
        )}

        {viewState === "compare" && selectedRecords.length === 2 && (
          <ComparisonView
            recordA={selectedRecords[0]}
            recordB={selectedRecords[1]}
            onBack={handleBack}
            onMergeComplete={handleMergeComplete}
          />
        )}

        {viewState === "success" && (
          <SuccessView onStartNew={handleStartNew} />
        )}
      </div>
    </PageWrapper>
  );
}
